import { FC } from "react";
import { useIntl } from "react-intl";
import { DefaultButton, SearchBox, Stack } from "@fluentui/react";
import { DropDown, Label } from "cayo.ui";
import { IObjectSelectionGrid } from "@cayo/shared/api/types/schema";
import { commonMessages } from "@cayo/shared/messages/common";
import { IMembersLookupData } from "../types";
import GlobalObjectSearchDialog from "./GlobalObjectSearchDialog";
import { globalObjectSearchMessages } from "./messages";
import { useGlobalObjectSearch } from "./useGlobalObjectSearch";

interface GlobalObjectSearchProps extends Pick<IObjectSelectionGrid, "globalSearchDescriptor"> {
  addObjects: (objects: any[]) => void;
  membersLookupData: IMembersLookupData;
}

const GlobalObjectSearch: FC<GlobalObjectSearchProps> = ({
  globalSearchDescriptor,
  membersLookupData,
  addObjects,
}) => {
  const { formatMessage } = useIntl();

  const {
    activeDialog,
    dismissDialog,
    dismissAllDialogs,
    globalSearch,
    setSearchScope,
    setSearchString,
    fetchGlobalSearch,
  } = useGlobalObjectSearch(globalSearchDescriptor!.lookupScope, membersLookupData, addObjects);

  if (!globalSearchDescriptor) {
    return <></>;
  }

  const { globalScopes, globalScopeTitle, title, tip } = globalSearchDescriptor;

  return (
    <>
      {activeDialog && (
        <GlobalObjectSearchDialog
          key={activeDialog.id}
          objects={activeDialog.lookupObjs}
          isDialogVisible={Boolean(activeDialog.tagName)}
          searchString={activeDialog.tagName}
          dismissDialog={dismissDialog}
          dismissAllDialogs={dismissAllDialogs}
          addObjects={addObjects}
        />
      )}
      <Stack horizontal tokens={{ childrenGap: 4 }} styles={{ root: { marginBottom: 8 } }}>
        <DropDown
          id="object-selection-global-search-dropdown"
          value={globalSearch.scope}
          options={globalScopes}
          setValue={(v) => setSearchScope(v)}
          label={globalScopeTitle || formatMessage(globalObjectSearchMessages.searchIn)}
          styles={{ root: { flex: 1 } }}
        />
        <Stack styles={{ root: { flex: 2 } }}>
          <Label
            htmlFor="object-selection-global-search-textfield"
            label={title || formatMessage(globalObjectSearchMessages.find)}
            tooltip={tip || formatMessage(globalObjectSearchMessages.tip)}
          />
          <Stack tokens={{ childrenGap: 4 }} horizontal>
            <SearchBox
              id="object-selection-global-search-textfield"
              ariaLabel={title || formatMessage(globalObjectSearchMessages.find)}
              value={globalSearch.string}
              onChange={(e, v) => setSearchString(v || "")}
              onSearch={() => {
                if (globalSearch.string) {
                  fetchGlobalSearch();
                }
              }}
              onClear={() => setSearchString("")}
              styles={{ root: { width: "100%", zIndex: 1 } }}
              placeholder={tip || formatMessage(globalObjectSearchMessages.tip)}
            />
            <DefaultButton
              text={formatMessage(commonMessages.add)}
              iconProps={{ iconName: "Add" }}
              onClick={fetchGlobalSearch}
              disabled={!globalSearch.string}
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default GlobalObjectSearch;
